/**
 * useCardTemplateSettings.js
 *
 * Admin hook for loading, editing, and saving ProductCard template settings.
 *
 * Settings live as props on `products`-type canvas nodes within the search
 * experience config. This hook reads the full config from the gateway, locates
 * products nodes for the selected view, and exposes a lightweight edit/save API.
 *
 * Runtime boundary contract (task-pack-05):
 *   cardTemplateMode  — 'preset' | 'custom'
 *   cardType          — 'standard' | 'horizontal' | 'simple'
 *   customCardTemplate — string | null
 *   cardPresentation  — validated store-theme presentation snapshot | null
 *
 * Save mode:
 *   save() — writes config to gateway in preview mode (draft)
 *
 * The shared UI Settings page flow owns preview rebuild and later production
 * publish. This hook must not call production save, buildAndPromote, or bridge
 * artifact publish directly.
 *
 * Validation rule:
 *   mode=custom requires a non-empty customCardTemplate before saving preview.
 */

import { useState, useEffect, useCallback } from 'react';
import { normalizeRuntimeSaveOutcome } from '../../../../runtime/saveOutcome';
import { useLocalizedMessage } from '../../../../../util/useLocalizedMessage';

export const CARD_TEMPLATE_MODES = {
  PRESET: 'preset',
  CUSTOM: 'custom',
};

export const CARD_PRESETS = ['standard', 'horizontal', 'simple'];

const DEFAULT_SETTINGS = {
  cardTemplateMode: CARD_TEMPLATE_MODES.PRESET,
  cardType: 'standard',
  customCardTemplate: null,
  cardPresentation: null,
  desktopProductsInRow: 3,
  mobileProductsInRow: 2,
};

const TEMPLATE_SETTING_KEYS = [
  'cardTemplateMode',
  'cardType',
  'customCardTemplate',
  'cardPresentation',
];

/**
 * Find all canvas nodes with type 'products' across all screens of a given view.
 *
 * @param {Object} config - Full search experience config
 * @param {string} view   - View key, e.g. 'resultsPage'
 * @returns {Array<{ screenId: string, nodeIndex: number, props: Object }>}
 */
function findProductsNodesInScreens(screens) {
  if (!screens) return [];

  const found = [];
  Object.entries(screens).forEach(([screenId, screenData]) => {
    const nodes = screenData?.canvas?.nodes;
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node, nodeIndex) => {
      if (node.type === 'products') {
        found.push({ screenId, nodeIndex, props: node.props || {} });
      }
    });
  });
  return found;
}

function findProductsNodes(config, view) {
  const viewConfig = config?.search?.views?.[view];
  return {
    desktop: findProductsNodesInScreens(viewConfig?.screens),
    mobile: findProductsNodesInScreens(viewConfig?.device?.mobile),
  };
}

/**
 * Extract template settings from the first products node found for a view.
 * Falls back to DEFAULT_SETTINGS when no products node exists.
 *
 * @param {Object} config
 * @param {string} view
 * @returns {{ cardTemplateMode, cardType, customCardTemplate }}
 */
function extractSettings(config, view) {
  const nodes = findProductsNodes(config, view);
  const templateProps = nodes.desktop[0]?.props || nodes.mobile[0]?.props || {};
  const desktopProps = nodes.desktop[0]?.props || {};
  const mobileProps = nodes.mobile[0]?.props || {};
  return {
    cardTemplateMode: templateProps.cardTemplateMode || DEFAULT_SETTINGS.cardTemplateMode,
    cardType: templateProps.cardType || DEFAULT_SETTINGS.cardType,
    customCardTemplate: templateProps.customCardTemplate ?? DEFAULT_SETTINGS.customCardTemplate,
    cardPresentation: templateProps.cardPresentation ?? DEFAULT_SETTINGS.cardPresentation,
    desktopProductsInRow: desktopProps.productsInRow || DEFAULT_SETTINGS.desktopProductsInRow,
    mobileProductsInRow: mobileProps.productsInRow || DEFAULT_SETTINGS.mobileProductsInRow,
  };
}

function updateProductsNodes(screens, templateUpdates, productsInRow) {
  if (!screens) return screens;

  return Object.fromEntries(
    Object.entries(screens).map(([screenId, screenData]) => {
      const nodes = screenData?.canvas?.nodes;
      if (!Array.isArray(nodes)) return [screenId, screenData];

      const updatedNodes = nodes.map((node) => {
        if (node.type !== 'products') return node;
        const props = {
          ...node.props,
          ...templateUpdates,
          productsInRow,
        };
        if (props.cardPresentation === null) delete props.cardPresentation;
        return {
          ...node,
          props,
        };
      });

      return [screenId, { ...screenData, canvas: { ...screenData.canvas, nodes: updatedNodes } }];
    }),
  );
}

/**
 * Apply updated template settings to all products nodes in the config for the
 * given view. Returns a new config object (shallow clone at the relevant depth).
 *
 * @param {Object} config
 * @param {string} view
 * @param {{ cardTemplateMode, cardType, customCardTemplate }} updates
 * @returns {Object} Updated config
 */
function applySettingsToConfig(config, view, updates) {
  const viewConfig = config?.search?.views?.[view];
  if (!viewConfig) return config;

  const templateUpdates = Object.fromEntries(
    TEMPLATE_SETTING_KEYS.map((key) => [key, updates[key]]),
  );
  const updatedScreens = updateProductsNodes(
    viewConfig.screens,
    templateUpdates,
    updates.desktopProductsInRow,
  );
  const updatedMobileScreens = updateProductsNodes(
    viewConfig.device?.mobile,
    templateUpdates,
    updates.mobileProductsInRow,
  );
  const updatedDevice = viewConfig.device?.mobile
    ? {
      ...viewConfig.device,
      mobile: updatedMobileScreens,
    }
    : viewConfig.device;

  return {
    ...config,
    search: {
      ...config.search,
      views: {
        ...config.search.views,
        [view]: {
          ...viewConfig,
          screens: updatedScreens,
          ...(updatedDevice ? { device: updatedDevice } : {}),
        },
      },
    },
  };
}

/**
 * Validate template settings before promoting to production.
 *
 * @param {{ cardTemplateMode, customCardTemplate }} settings
 * @returns {string | null} validation error message, or null if valid
 */
function validateForPreviewSave(settings, emptyTemplateMessage) {
  for (const value of [settings.desktopProductsInRow, settings.mobileProductsInRow]) {
    if (!Number.isInteger(value) || value < 1 || value > 6) {
      return 'Products per row must be a whole number between 1 and 6.';
    }
  }
  if (settings.cardTemplateMode === CARD_TEMPLATE_MODES.CUSTOM) {
    const tpl = typeof settings.customCardTemplate === 'string'
      ? settings.customCardTemplate.trim()
      : '';
    if (!tpl) {
      return emptyTemplateMessage;
    }
    if (settings.cardPresentation !== null && !isCardPresentation(settings.cardPresentation)) {
      return 'The imported storefront card style snapshot is invalid.';
    }
  }
  if (settings.cardPresentation !== null && settings.cardPresentation !== undefined) {
    const exactObject = (value, keys) => Boolean(value)
      && typeof value === 'object'
      && !Array.isArray(value)
      && Object.keys(value).length === keys.length
      && Object.keys(value).every(key => keys.includes(key));
    const pixelValue = (value, min, max) => {
      const match = /^([0-9]+(?:\.[0-9]{1,3})?)px$/.exec(String(value));
      return Boolean(match) && Number(match[1]) >= min && Number(match[1]) <= max;
    };
    const safeColor = (value) => /^#[0-9a-f]{6}$/i.test(String(value))
      || /^rgba?\(\s*[0-9]{1,3}\s*,\s*[0-9]{1,3}\s*,\s*[0-9]{1,3}(?:\s*,\s*(?:0|1|0?\.[0-9]{1,3}))?\s*\)$/i.test(String(value));
    const textKeys = ['color', 'fontFamily', 'fontSize', 'fontWeight', 'lineHeight'];
    const textStyle = (value) => exactObject(value, textKeys)
      && safeColor(value.color)
      && typeof value.fontFamily === 'string'
      && /^[A-Za-z0-9 ,_'"-]{1,160}$/.test(value.fontFamily)
      && pixelValue(value.fontSize, 6, 96)
      && /^(?:normal|bold|[1-9]00)$/.test(String(value.fontWeight))
      && (value.lineHeight === 'normal' || pixelValue(value.lineHeight, 1, 200));
    const presentation = settings.cardPresentation;
    const valid = exactObject(presentation, ['schemaVersion', 'desktop', 'mobile'])
      && presentation.schemaVersion === 1
      && ['desktop', 'mobile'].every((viewport) => {
        const entry = presentation[viewport];
        return exactObject(entry, ['card', 'title', 'price'])
          && exactObject(entry.card, ['borderRadius'])
          && pixelValue(entry.card.borderRadius, 0, 100)
          && textStyle(entry.title)
          && textStyle(entry.price);
      });
    if (!valid) return 'Detected category presentation contains an unsupported value.';
  }
  return null;
}

const PRESENTATION_VIEWPORTS = ['desktop', 'mobile'];
const PRESENTATION_ROLES = ['title', 'price'];
const PRESENTATION_TEXT_KEYS = ['color', 'fontFamily', 'fontSize', 'fontWeight', 'lineHeight'];

function exactObject(value, keys) {
  return Boolean(value)
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).length === keys.length
    && Object.keys(value).every((key) => keys.includes(key));
}

function boundedCssNumber(value, minimum, maximum) {
  const match = /^([0-9]+(?:\.[0-9]{1,3})?)px$/.exec(String(value));
  return Boolean(match) && Number(match[1]) >= minimum && Number(match[1]) <= maximum;
}

function safeColor(value) {
  if (/^#[0-9a-f]{6}$/i.test(String(value))) return true;
  const match = /^rgba?\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})(?:\s*,\s*(0|1|0?\.[0-9]{1,3}))?\s*\)$/i.exec(String(value));
  return Boolean(match) && match.slice(1, 4).every((channel) => Number(channel) <= 255);
}

function validPresentationText(value) {
  return exactObject(value, PRESENTATION_TEXT_KEYS)
    && safeColor(value.color)
    && /^[A-Za-z0-9 ,_'"-]{1,160}$/.test(value.fontFamily)
    && boundedCssNumber(value.fontSize, 6, 96)
    && /^(?:normal|bold|[1-9]00)$/.test(value.fontWeight)
    && (value.lineHeight === 'normal' || boundedCssNumber(value.lineHeight, 1, 200));
}

function isCardPresentation(value) {
  if (!exactObject(value, ['schemaVersion', ...PRESENTATION_VIEWPORTS]) || value.schemaVersion !== 1) {
    return false;
  }
  return PRESENTATION_VIEWPORTS.every((viewport) => {
    const entry = value[viewport];
    if (!exactObject(entry, ['card', ...PRESENTATION_ROLES])
      || !exactObject(entry.card, ['borderRadius'])
      || !boundedCssNumber(entry.card.borderRadius, 0, 100)) {
      return false;
    }
    return PRESENTATION_ROLES.every((role) => validPresentationText(entry[role]));
  });
}

/**
 * useCardTemplateSettings
 *
 * @param {Object} settingsClient — runtime-backed settings port
 * @param {string} shopId
 * @param {string} view          — selected surface/view (e.g. 'resultsPage')
 *
 * @returns {{
 *   settings: { cardTemplateMode, cardType, customCardTemplate },
 *   availableViews: string[],
 *   loading: boolean,
 *   error: string | null,
 *   hasChanges: boolean,
 *   isSaving: boolean,
 *   validationError: string | null,
 *   updateSettings: (patch: Object) => void,
 *   save: () => Promise<boolean>,
 *   reset: () => void,
 * }}
 */
export function useCardTemplateSettings(settingsClient, shopId, view) {
  const t = useLocalizedMessage();
  const [config, setConfig] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const availableViews = config?.search?.views ? Object.keys(config.search.views) : [];

  const load = useCallback(async () => {
    if (!settingsClient || !shopId) return;
    setLoading(true);
    setError(null);
    try {
      const loaded = await settingsClient.loadSearchExperienceConfig(shopId, 'preview');
      setConfig(loaded);
      const extracted = extractSettings(loaded, view);
      setSettings(extracted);
      setOriginalSettings(extracted);
      setHasChanges(false);
    } catch (err) {
      setError(err.message || t('search.settings.config.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [settingsClient, shopId, view, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-extract settings when view changes (config already loaded)
  useEffect(() => {
    if (!config) return;
    const extracted = extractSettings(config, view);
    setSettings(extracted);
    setOriginalSettings(extracted);
    setHasChanges(false);
    setValidationError(null);
  }, [view, config]);

  const updateSettings = useCallback((patch) => {
    // Keep the inactive mode's values as a draft. Switching modes is a
    // presentation choice, not an instruction to destroy a merchant's HTML.
    setSettings((prev) => ({ ...prev, ...patch }));
    setHasChanges(true);
    setValidationError(null);
  }, []);

  const save = useCallback(async () => {
    if (!settingsClient || !config) return;
    const vErr = validateForPreviewSave(settings, t('search.cardTemplate.emptySource'));
    if (vErr) {
      setValidationError(vErr);
      throw new Error(vErr);
    }
    setValidationError(null);
    setIsSaving(true);
    setError(null);
    try {
      const updatedConfig = applySettingsToConfig(config, view, settings);
      const result = await settingsClient.saveSearchExperienceConfig(shopId, updatedConfig, 'preview');
      const outcome = normalizeRuntimeSaveOutcome(result, updatedConfig);
      if (!outcome.success || outcome.errors.length > 0) {
        throw new Error(
          outcome.errors[0]?.message
          || result?.error
          || t('search.settings.config.saveRejected'),
        );
      }
      setConfig(outcome.config || updatedConfig);
      setOriginalSettings({ ...settings });
      setHasChanges(false);
      return true;
    } catch (err) {
      const message = err.message || t('search.settings.config.saveFailed');
      setError(message);
      throw new Error(message);
    } finally {
      setIsSaving(false);
    }
  }, [settingsClient, config, shopId, view, settings, t]);

  const reset = useCallback(() => {
    if (originalSettings) {
      setSettings({ ...originalSettings });
      setHasChanges(false);
      setValidationError(null);
    }
  }, [originalSettings]);

  return {
    settings,
    availableViews,
    loading,
    error,
    hasChanges,
    isSaving,
    validationError,
    updateSettings,
    save,
    reset,
  };
}
